import { styled } from '@linaria/react';
import { fonts, layout, primitives, semantic, shadow, size, space } from '../../theme/tokens';
import type { ComboboxSize } from './Combobox.types';

export { Chevron, SecondaryLabel } from '../Dropdown/Dropdown.styles';

/*
 * Full height where the container has one, because the list hangs from the bottom of this.
 * The field is only as tall as its text, so a wrapper that shrank to it dropped the list
 * inside the bar it sits in rather than under it. Where the container is content-sized, this
 * resolves to auto and the two are the same thing.
 */
export const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 1;

  &[data-open='true'] {
    z-index: ${layout.zIndexDropdown};
  }
`;

const HEIGHT_VAR = '--voyager-combobox-height';

export const Field = styled.div<{ $size: ComboboxSize }>`
  display: flex;
  align-items: center;
  gap: ${space['space-200']};
  box-sizing: border-box;
  width: 100%;
  min-width: 100px;
  /*
   * Content height by default, so the bar composing this decides it. Size says how big the type
   * is, nothing more: a fixed 40 or 56 here made a segment taller than the pill holding it, and
   * the consumer could only win it back by overriding the property above.
   */
  height: var(${HEIGHT_VAR}, auto);
  padding: 0 ${(p) => (p.$size === 'lg' ? space['space-400'] : space['space-300'])};
  border: ${size.stroke['stroke-default']} solid transparent;
  border-radius: ${size.radius['radius-sm']};
  background: transparent;
  color: ${semantic.text.primary};
  cursor: text;
  transition: border-color 0.15s ease, background 0.15s ease;

  /*
   * No box of its own: the only consumer is a search bar whose segment fills around it. The
   * border stays, transparent, so the error state has something to colour and nothing shifts.
   */
  &[data-error='true'] {
    border-color: ${semantic.border.danger};
  }

  &[data-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Not shared with Dropdown, which bands its rows full-width and has consumers of its own. */
export const OptionsList = styled.ul`
  position: absolute;
  top: calc(100% + var(--voyager-combobox-list-offset, ${space['space-100']}));
  right: 0;
  z-index: 200;
  box-sizing: border-box;
  margin: 0;
  padding: ${space['space-600']};
  list-style: none;
  background: ${semantic.bg.canvas};
  border: none;
  border-radius: ${size.radius['radius-xxl']};
  box-shadow: ${shadow.lg};
  overflow: hidden;
  max-height: 360px;
  white-space: nowrap;
  min-width: var(--voyager-combobox-list-min-width, 100%);
  display: flex;
  flex-direction: column;

  &[data-align='left'] {
    right: auto;
    left: 0;
  }
`;

export const OptionsContainer = styled.div`
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const Option = styled.li`
  display: flex;
  align-items: center;
  gap: ${space['space-300']};
  box-sizing: border-box;
  flex-shrink: 0;
  min-height: ${primitives.size['size-1400']};
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  padding: ${space['space-200']} ${space['space-400']};
  border-radius: ${size.radius['radius-md']};
  cursor: pointer;
  color: ${semantic.text.primary};
  transition: background 0.1s ease;

  &:hover,
  &[data-active='true'] {
    background: ${semantic.bg.surfaceSubtle};
  }

  &[data-selected='true'] {
    font-weight: ${fonts.weight.semiBold};
  }

  &[data-disabled='true'] {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

export const TextInput = styled.input<{ $size: ComboboxSize }>`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  padding: 0;
  background: transparent;
  font-family: ${fonts.body};
  font-size: ${(p) => (p.$size === 'lg' ? fonts.size.base : fonts.size.sm)};
  color: inherit;
  text-overflow: ellipsis;

  &::placeholder {
    color: ${semantic.text.tertiary};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${space['space-200']} 0;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const OptionSubtext = styled.span`
  color: ${semantic.text.secondary};
  font-size: ${fonts.size.sm};
`;

export const StatusRow = styled.li`
  display: flex;
  align-items: center;
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  padding: ${space['space-300']} ${space['space-600']};
  color: ${semantic.text.tertiary};
  cursor: default;

  &[data-tone='error'] {
    color: ${semantic.text.danger};
  }
`;

export const ListFooter = styled.div`
  flex-shrink: 0;
  padding: ${space['space-300']} ${space['space-600']} 0;
  border-top: ${size.stroke['stroke-default']} solid ${semantic.border.default};
  margin-top: ${space['space-300']};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;
